import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

type Instance = any;
type Profile = any;

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  instance: Instance | null;
  profiles: Profile[];
  onSaved?: () => void;
}

export function AdminNumberEditDialog({ open, onOpenChange, instance, profiles, onSaved }: Props) {
  const [nickname, setNickname] = useState("");
  const [accountType, setAccountType] = useState("Personal");
  const [profileId, setProfileId] = useState("");
  const [official, setOfficial] = useState(false);
  const [groups, setGroups] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (instance) {
      setNickname(instance.nickname || "");
      setAccountType(instance.account_type || "Personal");
      setProfileId(instance.profile_id || "");
      setOfficial(!!instance.is_official_contacts_active);
      setGroups(!!instance.is_warmup_groups_active);
    }
  }, [instance]);

  if (!instance) return null;

  const save = async () => {
    setSaving(true);
    try {
      const { error } = await supabase.functions.invoke("admin-stats", {
        body: {
          section: "update_instance",
          instance_id: instance.id,
          updates: {
            nickname: nickname || null,
            account_type: accountType,
            profile_id: profileId,
            is_official_contacts_active: official,
            is_warmup_groups_active: groups,
          },
        },
      });
      if (error) throw error;
      toast.success("Número atualizado");
      onSaved?.();
      onOpenChange(false);
    } catch (e: any) {
      toast.error(e.message || "Erro ao salvar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Editar Número (Admin)</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="text-xs text-muted-foreground font-mono">{instance.phone_number}</div>
          <div className="space-y-1.5">
            <Label>Apelido</Label>
            <Input value={nickname} onChange={(e) => setNickname(e.target.value)} placeholder="Apelido do número" />
          </div>
          <div className="space-y-1.5">
            <Label>Tipo de conta</Label>
            <Select value={accountType} onValueChange={setAccountType}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Personal">Pessoal</SelectItem>
                <SelectItem value="Business">Business</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Perfil de aquecimento</Label>
            <Select value={profileId} onValueChange={setProfileId}>
              <SelectTrigger><SelectValue placeholder="Selecionar..." /></SelectTrigger>
              <SelectContent>
                {profiles.map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.ui_icon || "⚙️"} {p.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center justify-between p-3 rounded-lg bg-secondary/30">
            <div>
              <p className="text-sm font-medium">Contatos oficiais</p>
              <p className="text-xs text-muted-foreground">Interagir com verificados</p>
            </div>
            <Switch checked={official} onCheckedChange={setOfficial} />
          </div>
          <div className="flex items-center justify-between p-3 rounded-lg bg-secondary/30">
            <div>
              <p className="text-sm font-medium">Grupos de aquecimento</p>
              <p className="text-xs text-muted-foreground">Participar de grupos do sistema</p>
            </div>
            <Switch checked={groups} onCheckedChange={setGroups} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={save} disabled={saving}>
            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Salvar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
